/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { jwtDecode } from 'jwt-decode'
import { dialogFallback } from './fallbackauthdialog'
import { graphApiCallAsync } from '../graph-api-call-async'
import { graphApiHandleClientSideErrors } from '../errors/graph-api-handler-client-side-errors'
import { GraphApiCallArgs } from '../graph-api-call-args'
import { logErrorToApi } from '../../common/error-logging'
import { handleGraphLoginError } from '../errors/graph-api-handler-login-errors'

/* globals OfficeRuntime */

let retryGetMiddletierToken = 0

export async function graphCallWithSsoWrapper(callback, args: GraphApiCallArgs): Promise<void> {
  try {
    const middletierToken: string = await getAccessToken({})
    let response: any = await graphApiCallAsync(middletierToken, args)
    if (!response) {
      throw new Error("Middle tier didn't respond")
    } else if (response.claims) {
      // Microsoft Graph requires an additional form of authentication. Have the Office host
      // get a new token using the Claims string, which tells AAD to prompt the user for all
      // required forms of authentication.
      const mfaMiddletierToken: string = await getAccessToken({
        authChallenge: response.claims,
      })
      response = await graphApiCallAsync(mfaMiddletierToken, args)
    }

    // AAD errors are returned to the client with HTTP code 200, so they do not trigger
    // the catch block below.
    if (response.data?.error) {
      handleAADErrors(response, callback, args)
    } else {
      callback(response)
    }
  } catch (exception) {
    // if handleClientSideErrors returns true then we will try to authenticate via the fallback
    // dialog rather than simply throw and error
    if (exception.isClientSideError) {
      if (graphApiHandleClientSideErrors(exception)) {
        dialogFallback(callback, args)
      } else {
        throw exception
      }
    } else {
      logErrorToApi('Graph API Error', 'Exception occurred during Graph API request', exception, args.url, {
        context: 'graph-api-call-with-sso-wrapper',
        operation: 'graphCallWithSsoWrapper',
        request: args,
      })

      if (exception.status == 403) {
        handleGraphLoginError('EXCEPTION: ' + JSON.stringify(exception))
      }

      throw exception
    }
  }
}

// Office's OfficeRuntime.auth.getAccessToken throws "Add-in is already requesting
// an access token." if invoked while a prior call is still pending. Serialize
// calls so a second invocation waits for the in-flight one to settle, then runs
// fresh. Only one promise reference is ever held.
let inFlightAccessToken: Promise<string> | null = null

// Office on the web intermittently throws transient SSO errors from getAccessToken even
// when Graph access is healthy: 13006 ("an unspecified error", only on the web) and 13008
// ("Office is still working on the last operation"). Treating either as a hard failure
// surfaces a bogus "Unable to access Microsoft Graph Api" to the user. Retry a few times
// with a short backoff before letting the error propagate.
const TRANSIENT_SSO_ERROR_CODES = [13006, 13008]
// Office.js intermittently throws internal argument-validation errors from getAccessToken
// (no numeric code) after a dialog completes or on a repeated call — see office-js #1389
// and #3097. These have no .code, so match them by message and treat them as transient too.
const TRANSIENT_SSO_ERROR_MESSAGES = ['Callback cannot be specified both in argument list']
const MAX_SSO_RETRIES = 5
const SSO_RETRY_DELAY_MS = 200
const SSO_RETRY_JITTER_MS = 50

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// Spreads retries by ±SSO_RETRY_JITTER_MS so concurrent callers don't all retry in lockstep.
function jitteredDelay(baseMs: number): number {
  return baseMs + Math.round(Math.random() * 2 * SSO_RETRY_JITTER_MS - SSO_RETRY_JITTER_MS)
}

function isTransientSsoError(exception): boolean {
  // Office throws these inconsistently: sometimes an object with .code/.message,
  // sometimes a bare string. Normalize to a searchable message before matching.
  const message = typeof exception === 'string' ? exception : (exception?.message ?? '')
  return TRANSIENT_SSO_ERROR_CODES.includes(exception?.code) || TRANSIENT_SSO_ERROR_MESSAGES.some((candidate) => message.includes(candidate))
}

// The bootstrap token returned by getAccessToken is a JWT valid for ~1 hour. Requesting a
// fresh one on every Graph call is what provokes Office's "already requesting a token" and
// argument-validation errors, so cache it in memory and reuse it until shortly before it
// expires. Cleared on logout (sidebar-logout-handler) and whenever the middle tier rejects
// the token as expired/invalid (handleAADErrors).
let cachedAccessToken: { token: string; expiresAtMs: number } | null = null
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

export function clearCachedAccessToken(): void {
  cachedAccessToken = null
}

function readCachedAccessToken(): string | null {
  if (cachedAccessToken && cachedAccessToken.expiresAtMs - TOKEN_REFRESH_BUFFER_MS > Date.now()) {
    return cachedAccessToken.token
  }
  return null
}

function cacheAccessToken(token: string): void {
  try {
    const exp = (jwtDecode(token) as { exp?: number })?.exp
    cachedAccessToken = exp ? { token, expiresAtMs: exp * 1000 } : null
  } catch {
    // Token isn't a decodable JWT — don't cache it; the next call fetches fresh.
    cachedAccessToken = null
  }
}

async function getAccessToken(args): Promise<string> {
  // A step-up (authChallenge) request must always reach Office to satisfy the challenge,
  // so only base requests are served from cache. Every successful fetch refreshes the cache.
  const canUseCache = !args?.authChallenge
  for (let attempt = 0; ; attempt++) {
    while (inFlightAccessToken) {
      try {
        await inFlightAccessToken
      } catch {
        // previous caller's error is theirs to handle
      }
    }

    // Re-checked each iteration (after any in-flight fetch settles) so concurrent cold
    // callers reuse the token the winner just cached instead of each fetching their own.
    if (canUseCache) {
      const cached = readCachedAccessToken()
      if (cached) {
        return cached
      }
    }

    let request: Promise<string> | null = null
    let response: string | null = null
    try {
      request = OfficeRuntime.auth.getAccessToken(args)
      inFlightAccessToken = request
      response = await request
      cacheAccessToken(response)
    } catch (exception) {
      if (isTransientSsoError(exception) && attempt < MAX_SSO_RETRIES) {
        await delay(jitteredDelay(SSO_RETRY_DELAY_MS * (attempt + 1)))
        continue
      }
      console.log('getAccessToken failed with exception.', { exception })
      throw {
        ...exception,
        isClientSideError: true,
      }
    } finally {
      if (inFlightAccessToken === request) {
        inFlightAccessToken = null
      }
    }
    return response
  }
}

function handleAADErrors(response: any, callback: any, args: GraphApiCallArgs): void {
  // On rare occasions the middle tier token is unexpired when Office validates it,
  // but expires by the time it is sent to AAD for exchange. AAD will respond
  // with 'The provided value for the 'assertion' is not valid. The assertion has expired.'
  // Retry the call of getAccessToken (no more than once). This time Office will return a
  // new unexpired middle tier token.

  if (response?.data?.error_description?.indexOf('AADSTS500133') !== -1 && retryGetMiddletierToken <= 0) {
    retryGetMiddletierToken++
    // The cached token is the expired assertion AAD just rejected — drop it so the retry
    // fetches a fresh one instead of replaying the same stale token.
    clearCachedAccessToken()
    graphCallWithSsoWrapper(callback, args)
  } else if (response.data?.error === 'invalid_grant') {
    clearCachedAccessToken()
    dialogFallback(callback, args)
    logErrorToApi('Graph API Error', 'Invalid grant', null, response.config.url, {
      context: 'graph-api-call-with-sso-wrapper',
      operation: 'graphCallWithSsoWrapper',
      requestData: response.config.data,
      requestUrl: response.config.url,
      requestMethod: response.config.method,
      responseStatus: response.config.status,
    })
  } else {
    logErrorToApi('Graph API Error', 'Unknown error', null, response.config.url, {
      context: 'graph-api-call-with-sso-wrapper',
      operation: 'graphCallWithSsoWrapper',
      requestData: response.config.data,
      requestUrl: response.config.url,
      requestMethod: response.config.method,
      responseStatus: response.config.status,
    })
  }
}
